/**
 * ASYCO Variant Price Sync — asyco-variant-price.js
 *
 * Objetivo:
 *   Cuando el usuario cambia de variante en la PDP, actualizar en vivo:
 *     - Precio contado (.asyco-contado-value)
 *     - Precio cuotas (.asyco-cuotas-value)
 *     - Compare-at price (.compare-at-price)
 *     - Badge de descuento (-XX%)
 *
 * Por qué existe:
 *   El metafield custom.precio_contado ahora vive por VARIANTE. Liquid
 *   server-side solo puede renderizar la variante inicial. Al cambiar
 *   de variante, Horizon actualiza el precio de cuotas nativamente,
 *   pero NO el de contado (porque no conoce nuestro metafield custom).
 *   Este script lo resuelve leyendo el mapa que Liquid inyecta en la
 *   página: <script data-asyco-variant-prices>.
 *
 * Eventos de Horizon 3.4 que escuchamos:
 *   - variant:selected  → notificación temprana con el ID de variante
 *   - variant:update    → confirmación tras recarga de la sección
 *
 * Fuentes de datos, por orden de prioridad:
 *   1. Mapa inyectado por Liquid con los precios por variante (instantáneo)
 *   2. Evento de Horizon (viene con e.detail.variant)
 *   3. Fallback: leer data-selected-variant-id del DOM
 */

(function () {
  'use strict';

  var LOG_PREFIX = '[ASYCO variant-price]';
  var DEBUG = false;  // Poner true para ver logs en consola

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }

  /* ─── Formateo de dinero en COP (sin decimales, con puntos) ────────── */
  function formatMoney(centavos) {
    var pesos = Math.round(Number(centavos) / 100);
    return '$' + pesos.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /* ─── Cargar el mapa de precios inyectado por Liquid ───────────────── */
  function getVariantPriceMap() {
    var maps = {};
    var scripts = document.querySelectorAll('script[data-asyco-variant-prices]');
    scripts.forEach(function (s) {
      try {
        var data = JSON.parse(s.textContent);
        if (data && data.productId && data.variants) {
          maps[data.productId] = data.variants;
        }
      } catch (e) {
        log('Error parseando mapa de variantes:', e);
      }
    });
    return maps;
  }

  /* ─── Actualizar el DOM con los precios de una variante ─────────────── */
  function actualizarPrecios(container, variantData) {
    if (!container || !variantData) return;

    var contadoEl = container.querySelector('[data-asyco-contado]');
    var cuotasEl = container.querySelector('[data-asyco-cuotas]');
    var compareEl = container.querySelector('[data-asyco-compare]');
    var compareRow = container.querySelector('[data-asyco-compare-row]');
    var discountEl = container.querySelector('[data-asyco-discount]');
    var contadoRow = container.querySelector('[data-asyco-contado-row]');

    // 1) Precio contado
    if (contadoEl && variantData.contado != null) {
      contadoEl.textContent = formatMoney(variantData.contado);
    }

    // 2) Precio cuotas (el "principal" / regular de Shopify)
    if (cuotasEl && variantData.price != null) {
      cuotasEl.textContent = formatMoney(variantData.price);
    }

    // 3) Compare-at price + badge descuento
    var hasCompare = variantData.compareAt && variantData.compareAt > variantData.contado;
    if (compareRow) {
      if (hasCompare) {
        compareRow.style.display = '';
        if (compareEl) compareEl.textContent = formatMoney(variantData.compareAt);
        if (discountEl) {
          var pct = Math.round(((variantData.compareAt - variantData.contado) / variantData.compareAt) * 100);
          if (pct > 0) {
            discountEl.textContent = '-' + pct + '%';
            discountEl.style.display = '';
          } else {
            discountEl.style.display = 'none';
          }
        }
      } else {
        compareRow.style.display = 'none';
      }
    }

    // 4) Mostrar/ocultar fila contado según disponibilidad de precio
    if (contadoRow) {
      contadoRow.style.display = (variantData.contado > 0) ? '' : 'none';
    }

    // Guardar el ID de variante actual en el contenedor
    container.setAttribute('data-selected-variant-id', variantData.id || '');

    log('Precios actualizados:', variantData);
  }

  /* ─── Sincronizar todos los contenedores con la variante dada ──────── */
  function syncVariant(variantId, productId) {
    if (!variantId) return;
    var maps = getVariantPriceMap();
    var variants = productId ? maps[productId] : null;

    // Si no hay productId, buscar el primer mapa que tenga esta variantId
    if (!variants) {
      Object.keys(maps).forEach(function (pid) {
        if (maps[pid][variantId] && !variants) variants = maps[pid];
      });
    }

    if (!variants || !variants[variantId]) {
      log('Variante no encontrada en el mapa:', variantId);
      return;
    }

    var variantData = Object.assign({ id: variantId }, variants[variantId]);

    // Actualizar todos los contenedores .asyco-pdp-prices del producto
    document.querySelectorAll('[data-asyco-price-container]').forEach(function (container) {
      // Solo actualizar los contenedores del mismo producto (si está declarado)
      var containerProductId = container.getAttribute('data-product-id');
      if (productId && containerProductId && String(containerProductId) !== String(productId)) {
        return;
      }
      actualizarPrecios(container, variantData);
    });
  }

  /* ─── Extraer variantId del evento de Horizon ───────────────────────── */
  function extraerVariantId(evt) {
    if (!evt || !evt.detail) return null;
    var d = evt.detail;

    // Formato Horizon 3.4: detail.variant.id
    if (d.variant && d.variant.id) return d.variant.id;
    // Formato alternativo: detail.resource.selected_variant.id
    if (d.resource && d.resource.selected_variant && d.resource.selected_variant.id) {
      return d.resource.selected_variant.id;
    }
    // Formato simple: detail.variantId
    if (d.variantId) return d.variantId;
    // Último recurso: detail.id
    if (typeof d.id === 'number' || typeof d.id === 'string') return d.id;

    return null;
  }

  function extraerProductId(evt) {
    if (!evt || !evt.detail) return null;
    var d = evt.detail;
    if (d.resource && d.resource.id) return d.resource.id;
    if (d.productId) return d.productId;
    if (d.product && d.product.id) return d.product.id;
    return null;
  }

  /* ─── Handler principal de cambio de variante ──────────────────────── */
  function onVariantChange(evt) {
    var variantId = extraerVariantId(evt);
    var productId = extraerProductId(evt);
    log('Evento recibido:', evt.type, 'variantId:', variantId, 'productId:', productId);
    if (variantId) {
      syncVariant(variantId, productId);
    } else {
      // Si no pudimos extraer el ID del evento, leer de la URL como fallback
      var urlParams = new URLSearchParams(window.location.search);
      var urlVariant = urlParams.get('variant');
      if (urlVariant) {
        log('Usando variantId de URL:', urlVariant);
        syncVariant(urlVariant, null);
      }
    }
  }

  /* ─── Fallback: observar cambios de URL (?variant=XYZ) ─────────────── */
  var lastUrl = window.location.href;
  function observarURL() {
    setInterval(function () {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        var urlParams = new URLSearchParams(window.location.search);
        var urlVariant = urlParams.get('variant');
        if (urlVariant) {
          log('URL cambió, nueva variante:', urlVariant);
          syncVariant(urlVariant, null);
        }
      }
    }, 500);
  }

  /* ─── Init ───────────────────────────────────────────────────────────── */
  function init() {
    // Escuchar los dos eventos que Horizon dispara al cambiar variante
    document.addEventListener('variant:selected', onVariantChange);
    document.addEventListener('variant:update', onVariantChange);

    // Eventos más antiguos por compatibilidad
    document.addEventListener('on:variant:change', onVariantChange);
    document.addEventListener('product:variant:change', onVariantChange);

    // Fallback por URL
    observarURL();

    // Sincronización inicial: si la URL ya trae ?variant=XYZ, asegurar que el
    // DOM muestre esa variante (Liquid pudo haber renderizado otra)
    var urlParams = new URLSearchParams(window.location.search);
    var initialVariant = urlParams.get('variant');
    if (initialVariant) {
      syncVariant(initialVariant, null);
    }

    log('Iniciado. Maps cargados:', Object.keys(getVariantPriceMap()).length);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // API pública para debug/testing
  window.ASYCO_VARIANT_PRICE = {
    sync: syncVariant,
    getMap: getVariantPriceMap,
    setDebug: function (v) { DEBUG = !!v; }
  };
})();